import { ref, watch, nextTick, onBeforeUnmount, type Ref } from 'vue'

/**
 * Composable-функция usePanZoom для управления масштабированием и панорамированием
 * изображений во встроенном вьювере.
 *
 * @param {Ref<boolean>} isViewDialogOpen - Реактивная ссылка на открытость диалога просмотра
 */
export function usePanZoom(isViewDialogOpen: Ref<boolean>) {
  const zoomFactor = ref(1)
  const translateX = ref(0)
  const translateY = ref(0)
  const isTransitioning = ref(false)

  // Состояние для тач-жестов
  const touchStartDist = ref(0)
  const startZoom = ref(1)
  const startCenterX = ref(0)
  const startCenterY = ref(0)
  const startTx = ref(0)
  const startTy = ref(0)

  // Состояние для десктопного перетаскивания (drag)
  const isDragging = ref(false)
  const dragStartX = ref(0)
  const dragStartY = ref(0)
  const dragStartTx = ref(0)
  const dragStartTy = ref(0)

  const viewerContainerRef = ref<HTMLDivElement | null>(null)

  /**
   * Обработчик touchstart для жестов на мобильных (pinch-to-zoom + pan)
   */
  const onTouchStart = (e: TouchEvent) => {
    if (!viewerContainerRef.value) return

    if (e.touches.length === 1) {
      // Одиночный тач — перетаскивание
      isDragging.value = true
      const touch = e.touches[0]
      dragStartX.value = touch.clientX
      dragStartY.value = touch.clientY
      dragStartTx.value = translateX.value
      dragStartTy.value = translateY.value
    } else if (e.touches.length === 2) {
      // Мультитач — pinch-to-zoom + pan
      isDragging.value = false // Отменяем drag одним пальцем
      
      const t1 = e.touches[0]
      const t2 = e.touches[1]
      const dx = t2.clientX - t1.clientX
      const dy = t2.clientY - t1.clientY
      touchStartDist.value = Math.sqrt(dx * dx + dy * dy)
      startZoom.value = zoomFactor.value

      const scx = (t1.clientX + t2.clientX) / 2
      const scy = (t1.clientY + t2.clientY) / 2
      const rect = viewerContainerRef.value.getBoundingClientRect()
      const cx = rect.width / 2
      const cy = rect.height / 2
      startCenterX.value = scx - rect.left - cx
      startCenterY.value = scy - rect.top - cy

      startTx.value = translateX.value
      startTy.value = translateY.value
    }
  }

  /**
   * Обработчик touchmove для жестов на мобильных (pinch-to-zoom + pan)
   */
  const onTouchMove = (e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault()
    }

    if (!viewerContainerRef.value) return

    if (e.touches.length === 1 && isDragging.value) {
      const touch = e.touches[0]
      const dx = touch.clientX - dragStartX.value
      const dy = touch.clientY - dragStartY.value
      translateX.value = dragStartTx.value + dx
      translateY.value = dragStartTy.value + dy
    } else if (e.touches.length === 2 && touchStartDist.value > 0) {
      const t1 = e.touches[0]
      const t2 = e.touches[1]
      const dx = t2.clientX - t1.clientX
      const dy = t2.clientY - t1.clientY
      const currentDist = Math.sqrt(dx * dx + dy * dy)

      const scx = (t1.clientX + t2.clientX) / 2
      const scy = (t1.clientY + t2.clientY) / 2
      const rect = viewerContainerRef.value.getBoundingClientRect()
      const cx = rect.width / 2
      const cy = rect.height / 2
      const currentCenterX = scx - rect.left - cx
      const currentCenterY = scy - rect.top - cy

      let newZoom = startZoom.value * (currentDist / touchStartDist.value)
      newZoom = Math.min(Math.max(newZoom, 0.5), 20)

      const txAfterZoom = startTx.value * (newZoom / startZoom.value) + startCenterX.value * (1 - newZoom / startZoom.value)
      const tyAfterZoom = startTy.value * (newZoom / startZoom.value) + startCenterY.value * (1 - newZoom / startZoom.value)

      translateX.value = Number((txAfterZoom + (currentCenterX - startCenterX.value)).toFixed(2))
      translateY.value = Number((tyAfterZoom + (currentCenterY - startCenterY.value)).toFixed(2))
      zoomFactor.value = Number(newZoom.toFixed(2))
    }
  }

  /**
   * Обработчик touchend
   */
  const onTouchEnd = () => {
    isDragging.value = false
    touchStartDist.value = 0
  }

  /**
   * Обработчик wheel для прокрутки мыши (зум в точку курсора)
   */
  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    if (!viewerContainerRef.value) return

    const rect = viewerContainerRef.value.getBoundingClientRect()
    const cx = rect.width / 2
    const cy = rect.height / 2
    const mouseX = e.clientX - rect.left - cx
    const mouseY = e.clientY - rect.top - cy

    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
    const oldZoom = zoomFactor.value
    let newZoom = oldZoom * factor
    newZoom = Math.min(Math.max(newZoom, 0.5), 20)

    translateX.value = Number((translateX.value * (newZoom / oldZoom) + mouseX * (1 - newZoom / oldZoom)).toFixed(2))
    translateY.value = Number((translateY.value * (newZoom / oldZoom) + mouseY * (1 - newZoom / oldZoom)).toFixed(2))
    zoomFactor.value = Number(newZoom.toFixed(2))
  }

  /**
   * Обработчики событий мыши для десктопного перетаскивания (drag)
   */
  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return
    isDragging.value = true
    dragStartX.value = e.clientX
    dragStartY.value = e.clientY
    dragStartTx.value = translateX.value
    dragStartTy.value = translateY.value
  }

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging.value) return
    const dx = e.clientX - dragStartX.value
    const dy = e.clientY - dragStartY.value
    translateX.value = dragStartTx.value + dx
    translateY.value = dragStartTy.value + dy
  }

  const onMouseUp = () => {
    isDragging.value = false
  }

  /**
   * Увеличивает масштаб изображения (от центра)
   */
  const zoomIn = () => {
    isTransitioning.value = true
    const oldZoom = zoomFactor.value
    const newZoom = Math.min(zoomFactor.value * 1.4, 20)
    translateX.value = Number((translateX.value * (newZoom / oldZoom)).toFixed(2))
    translateY.value = Number((translateY.value * (newZoom / oldZoom)).toFixed(2))
    zoomFactor.value = Number(newZoom.toFixed(2))
    setTimeout(() => {
      isTransitioning.value = false
    }, 200)
  }

  /**
   * Уменьшает масштаб изображения (к центру)
   */
  const zoomOut = () => {
    isTransitioning.value = true
    const oldZoom = zoomFactor.value
    const newZoom = Math.max(zoomFactor.value / 1.4, 0.5)
    translateX.value = Number((translateX.value * (newZoom / oldZoom)).toFixed(2))
    translateY.value = Number((translateY.value * (newZoom / oldZoom)).toFixed(2))
    zoomFactor.value = Number(newZoom.toFixed(2))
    setTimeout(() => {
      isTransitioning.value = false
    }, 200)
  }

  /**
   * Сбрасывает масштаб и смещение к исходному
   */
  const zoomReset = () => {
    isTransitioning.value = true
    zoomFactor.value = 1
    translateX.value = 0
    translateY.value = 0
    setTimeout(() => {
      isTransitioning.value = false
    }, 200)
  }

  /**
   * Регистрация и удаление non-passive обработчиков touch/wheel событий
   */
  const cleanupListeners = () => {
    if (viewerContainerRef.value) {
      viewerContainerRef.value.removeEventListener('touchstart', onTouchStart)
      viewerContainerRef.value.removeEventListener('touchmove', onTouchMove)
      viewerContainerRef.value.removeEventListener('touchend', onTouchEnd)
      viewerContainerRef.value.removeEventListener('touchcancel', onTouchEnd)
      viewerContainerRef.value.removeEventListener('wheel', onWheel)
      viewerContainerRef.value.removeEventListener('mousedown', onMouseDown)
      viewerContainerRef.value.removeEventListener('mousemove', onMouseMove)
      viewerContainerRef.value.removeEventListener('mouseup', onMouseUp)
      viewerContainerRef.value.removeEventListener('mouseleave', onMouseUp)
    }
  }

  const setupListeners = () => {
    if (viewerContainerRef.value) {
      viewerContainerRef.value.addEventListener('touchstart', onTouchStart, { passive: false })
      viewerContainerRef.value.addEventListener('touchmove', onTouchMove, { passive: false })
      viewerContainerRef.value.addEventListener('touchend', onTouchEnd, { passive: false })
      viewerContainerRef.value.addEventListener('touchcancel', onTouchEnd, { passive: false })
      viewerContainerRef.value.addEventListener('wheel', onWheel, { passive: false })
      viewerContainerRef.value.addEventListener('mousedown', onMouseDown)
      viewerContainerRef.value.addEventListener('mousemove', onMouseMove)
      viewerContainerRef.value.addEventListener('mouseup', onMouseUp)
      viewerContainerRef.value.addEventListener('mouseleave', onMouseUp)
    }
  }

  watch(isViewDialogOpen, async (isOpen) => {
    if (isOpen) {
      zoomFactor.value = 1
      translateX.value = 0
      translateY.value = 0
      await nextTick()
      setupListeners()
    } else {
      cleanupListeners()
    }
  })

  onBeforeUnmount(() => {
    cleanupListeners()
  })

  return {
    zoomFactor,
    translateX,
    translateY,
    isTransitioning,
    viewerContainerRef,
    zoomIn,
    zoomOut,
    zoomReset
  }
}
